import React from "react";
import { Modal } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { BillingCycle, Plan, PlanId } from "../subscription.types";

interface PlanComparisonModalProps {
  open: boolean;
  plans: Plan[];
  cycle: BillingCycle;
  currentPlanId: PlanId | null;
  onClose: () => void;
}

const cap = (n: number | null | undefined, t: TFunction): React.ReactNode =>
  n == null ? t("planComparison.unlimited") : n;

const priceCell = (p: Plan, cycle: BillingCycle, t: TFunction): React.ReactNode => {
  if (p.isFree)
    return <span className="sub-compare-price">{t("planComparison.free")}</span>;
  if (p.customPricing)
    return <span className="sub-compare-price">{t("planComparison.custom")}</span>;
  const amt = cycle === "yearly" ? p.yearlyMonthlyPrice : p.monthlyPrice;
  if (p.includedWorkingUsers != null) {
    return (
      <>
        <span className="sub-compare-price">${amt}</span>
        <span className="sub-compare-per"> {t("planComparison.perMonth")}</span>
      </>
    );
  }
  return (
    <>
      <span className="sub-compare-price">${amt}</span>
      <span className="sub-compare-per"> {t("planComparison.perUserPerMonth")}</span>
    </>
  );
};

const includedUsersCell = (p: Plan): React.ReactNode =>
  p.includedWorkingUsers != null ? p.includedWorkingUsers : "—";

const additionalUserCell = (p: Plan, cycle: BillingCycle): React.ReactNode => {
  const extra =
    cycle === "yearly" ? p.additionalUserPriceYearly : p.additionalUserPrice;
  return extra != null ? `$${extra}/mo` : "—";
};

const yes = <CheckOutlined className="sub-compare-yes" />;
const no = <CloseOutlined className="sub-compare-no" />;

const buildRows = (
  t: TFunction,
): { label: string; render: (p: Plan, c: BillingCycle) => React.ReactNode }[] => [
  { label: t("planComparison.rows.price"), render: (p, c) => priceCell(p, c, t) },
  {
    label: t("planComparison.rows.workingUsersIncluded"),
    render: (p) => includedUsersCell(p),
  },
  {
    label: t("planComparison.rows.additionalWorkingUser"),
    render: (p, c) => additionalUserCell(p, c),
  },
  {
    label: t("planComparison.rows.activeProjects"),
    render: (p) => cap(p.concurrentProjectLimit, t),
  },
  {
    label: t("planComparison.rows.maxWorkingUsers"),
    render: (p) => cap(p.workingUserLimit, t),
  },
  {
    label: t("planComparison.rows.contributorsViewers"),
    render: () => t("planComparison.rows.contributorsViewersValue"),
  },
  {
    label: t("planComparison.rows.aiCreditsPerMonth"),
    render: (p) => (p.monthlyCredits ? p.monthlyCredits.toLocaleString() : "—"),
  },
  {
    label: t("planComparison.rows.exportPublishShare"),
    render: (p) => (p.isFree ? no : yes),
  },
  {
    label: t("planComparison.rows.aiFeatures"),
    render: () => (
      <>
        {yes}{" "}
        <span className="sub-compare-note">
          {t("planComparison.rows.usesCredits")}
        </span>
      </>
    ),
  },
];

const PlanComparisonModal: React.FC<PlanComparisonModalProps> = ({
  open,
  plans,
  cycle,
  currentPlanId,
  onClose,
}) => {
  const { t } = useTranslation("subscription");
  const rows = buildRows(t);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={860}
      className="sub-modal"
      title={null}
    >
      <h3 className="sub-modal-title">{t("planComparison.title")}</h3>
      <p className="sub-modal-sub">
        {t("planComparison.subtitle", {
          cycle:
            cycle === "yearly" ? t("common.yearly") : t("common.monthly"),
        })}
      </p>

      <div className="sub-compare-wrap">
        <table className="sub-compare">
          <thead>
            <tr>
              <th />
              {plans.map((p) => (
                <th key={p.id} className={p.id === currentPlanId ? "current" : ""}>
                  <span className="sub-compare-plan">{p.name}</span>
                  {p.mostPopular && (
                    <span className="sub-compare-badge">
                      {t("common.mostPopular")}
                    </span>
                  )}
                  {p.id === currentPlanId && (
                    <span className="sub-compare-badge current">
                      {t("planComparison.current")}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="sub-compare-rowlabel">{row.label}</td>
                {plans.map((p) => (
                  <td
                    key={p.id}
                    className={p.id === currentPlanId ? "current" : ""}
                  >
                    {row.render(p, cycle)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="sub-compare-footnote">
        <strong>{t("planComparison.footnote.workingUsersLabel")}</strong>{" "}
        {t("planComparison.footnote.part1")}{" "}
        <strong>{t("planComparison.footnote.contributorsViewersLabel")}</strong>{" "}
        {t("planComparison.footnote.part2")}
      </p>

      <div className="sub-modal-actions">
        <button className="sub-btn sub-btn-primary" onClick={onClose}>
          {t("planComparison.gotIt")}
        </button>
      </div>
    </Modal>
  );
};

export default PlanComparisonModal;
